import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Avatar,
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  useDisclosure,
} from '@nextui-org/react';
import {
  AlertCircle,
  ArrowLeft,
  CalendarClock,
  Crown,
  DoorOpen,
  Gamepad2,
  Lock,
  LogIn,
  LogOut,
  MessageCircle,
  Pencil,
  Send,
  Swords,
  Trash2,
  User as UserIcon,
  UserPlus,
  Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import communityService from '../services/communityService';
import chatService from '../services/chatService';
import lobbyService, { Lobby, LobbyMessage } from '../services/lobbyService';
import { LobbyStatusBadge } from '../components/community/LobbyCard';
import LobbyFormModal from '../components/community/LobbyFormModal';

const ACCENT = '#3fced1';
const CHAT_POLL_INTERVAL = 10000; // 10s

const LobbyDetailPage: React.FC = () => {
  const { lobbyId } = useParams<{ lobbyId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const currentUserId = user?.id || null;

  const editModal = useDisclosure();
  const deleteModal = useDisclosure();

  const [lobby, setLobby] = useState<Lobby | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const prevMessageCountRef = useRef<number>(0);
  const lobbyIdRef = useRef<string | null>(null);
  lobbyIdRef.current = lobbyId ?? null;

  const isMember = lobbyService.isMember(lobby, currentUserId);
  const isOwner = lobbyService.isOwner(lobby, currentUserId);
  const memberCount = lobbyService.getMemberCount(lobby);
  const maxMembers = lobby?.maxMembers ?? 8;
  const isFull = lobby?.status === 'full' || memberCount >= maxMembers;
  const isClosed = lobby?.status === 'closed';

  // ================================================================
  // Laden + Polling
  // ================================================================

  const loadLobby = useCallback(
    async (silent: boolean = false) => {
      if (!lobbyId) return;
      if (!silent) {
        setLoading(true);
        setNotFound(false);
        setLoadError(null);
      }
      try {
        const result = await lobbyService.getLobbyById(lobbyId);
        if (lobbyIdRef.current !== lobbyId) return;
        if (!result) {
          if (!silent) setNotFound(true);
          return;
        }
        setLobby(result);
      } catch (err: any) {
        if (lobbyIdRef.current !== lobbyId) return;
        if (!silent) {
          setLoadError(
            lobbyService.extractErrorMessage(err, 'Lobby konnte nicht geladen werden.')
          );
        }
      } finally {
        if (!silent && lobbyIdRef.current === lobbyId) setLoading(false);
      }
    },
    [lobbyId]
  );

  useEffect(() => {
    prevMessageCountRef.current = 0;
    loadLobby();
    window.scrollTo({ top: 0 });
  }, [loadLobby]);

  // Polling (10s) — nur für Mitglieder relevant (Chat), schadet aber generell nicht
  useEffect(() => {
    if (!lobbyId) return;
    const interval = setInterval(() => loadLobby(true), CHAT_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [lobbyId, loadLobby]);

  // Auto-Scroll bei neuen Nachrichten
  const messages: LobbyMessage[] = Array.isArray(lobby?.messages) ? lobby!.messages! : [];
  useEffect(() => {
    if (!isMember) return;
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({
        behavior: prevMessageCountRef.current === 0 ? 'auto' : 'smooth',
      });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, isMember]);

  // ================================================================
  // Aktionen
  // ================================================================

  const handleJoin = async () => {
    if (!lobby?.id) return;
    if (!isAuthenticated) {
      setShowLoginHint(true);
      return;
    }
    setActionLoading(true);
    setActionError(null);
    try {
      const updated = await lobbyService.joinLobby(lobby.id);
      if (updated) setLobby(updated);
    } catch (err: any) {
      setActionError(
        lobbyService.extractErrorMessage(err, 'Beitritt fehlgeschlagen.')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!lobby?.id) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const updated = await lobbyService.leaveLobby(lobby.id);
      if (updated) setLobby(updated);
    } catch (err: any) {
      setActionError(
        lobbyService.extractErrorMessage(err, 'Verlassen fehlgeschlagen.')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleClosed = async () => {
    if (!lobby?.id) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const updated = await lobbyService.updateLobby(lobby.id, {
        status: isClosed ? 'open' : 'closed',
      });
      if (updated) setLobby(updated);
    } catch (err: any) {
      setActionError(
        lobbyService.extractErrorMessage(err, 'Status konnte nicht geändert werden.')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!lobby?.id) return;
    setDeleting(true);
    try {
      await lobbyService.deleteLobby(lobby.id);
      navigate('/community/lobbys');
    } catch (err: any) {
      setActionError(
        lobbyService.extractErrorMessage(err, 'Lobby konnte nicht gelöscht werden.')
      );
      deleteModal.onClose();
    } finally {
      setDeleting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!lobby?.id) return;
    const content = messageInput.trim();
    if (!content || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const updated = await lobbyService.sendMessage(lobby.id, content);
      if (updated) setLobby(updated);
      setMessageInput('');
    } catch (err: any) {
      setSendError(
        lobbyService.extractErrorMessage(err, 'Nachricht konnte nicht gesendet werden.')
      );
    } finally {
      setSending(false);
    }
  };

  // ================================================================
  // Render
  // ================================================================

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-zinc-950">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (notFound || (!lobby && !loadError)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 bg-zinc-950">
        <Helmet>
          <title>Lobby nicht gefunden | DartCloud</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center max-w-md">
          <Swords className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Lobby nicht gefunden</h2>
          <p className="text-gray-400 mb-6 text-sm">
            Diese Lobby existiert nicht mehr oder wurde gelöscht.
          </p>
          <Button
            as={RouterLink}
            to="/community/lobbys"
            variant="bordered"
            className="font-semibold text-white border-zinc-700"
            startContent={<ArrowLeft size={16} />}
          >
            Zu allen Lobbies
          </Button>
        </div>
      </div>
    );
  }

  if (!lobby) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 bg-zinc-950">
        <Helmet>
          <title>Lobby | DartCloud</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-sm text-gray-300 mb-6">{loadError}</p>
          <Button
            variant="bordered"
            className="font-semibold text-white border-zinc-700"
            onPress={() => loadLobby()}
          >
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  const ownerAvatarUrl = communityService.buildThumbnailUrl(lobby.ownerImage, 100);
  const scheduled = lobbyService.formatScheduledAt(lobby.scheduledAt);
  const statusColor = lobbyService.getStatusColor(lobby.status);
  const tags = Array.isArray(lobby.tags) ? lobby.tags : [];
  const members = Array.isArray(lobby.members) ? lobby.members : [];

  return (
    <>
      <Helmet>
        <title>{lobby.name ?? 'Lobby'} - Spiel-Lobby | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-zinc-950 text-white">
        {/* ============ Kopfbereich ============ */}
        <section className="relative overflow-hidden border-b border-zinc-800/80">
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
          <div
            className="absolute -top-28 left-1/3 w-[480px] h-[280px] rounded-full blur-3xl opacity-15 pointer-events-none"
            style={{ backgroundColor: statusColor }}
          />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-12">
            <RouterLink
              to="/community/lobbys"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft size={16} />
              Alle Lobbies
            </RouterLink>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap mb-3">
                  <LobbyStatusBadge lobby={lobby} />
                  {lobby.isPublic === false && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500">
                      <Lock size={12} /> Private Lobby
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight break-words">
                  {lobby.name ?? 'Unbenannte Lobby'}
                </h1>
                {lobby.description && (
                  <p className="mt-3 text-sm md:text-base text-gray-300 leading-relaxed whitespace-pre-wrap max-w-2xl">
                    {lobby.description}
                  </p>
                )}

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {lobby.gameMode && (
                    <Chip
                      size="sm"
                      variant="flat"
                      startContent={<Gamepad2 size={13} style={{ color: ACCENT }} />}
                      classNames={{
                        base: 'h-7 px-2.5 border',
                        content: 'text-xs font-semibold px-1',
                      }}
                      style={{
                        backgroundColor: `${ACCENT}14`,
                        borderColor: `${ACCENT}33`,
                        color: ACCENT,
                      }}
                    >
                      {lobby.gameMode}
                    </Chip>
                  )}
                  {lobby.platform && (
                    <Chip
                      size="sm"
                      variant="flat"
                      classNames={{
                        base: 'h-7 px-2.5 bg-zinc-800 border border-zinc-700',
                        content: 'text-xs text-gray-300 px-1',
                      }}
                    >
                      {lobby.platform}
                    </Chip>
                  )}
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      size="sm"
                      variant="flat"
                      classNames={{
                        base: 'h-7 px-2.5 bg-zinc-800 border border-zinc-700',
                        content: 'text-xs text-gray-400 px-1',
                      }}
                    >
                      #{tag}
                    </Chip>
                  ))}
                </div>

                <div className="mt-4 flex items-center gap-4 flex-wrap text-sm text-gray-400">
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={15} style={{ color: ACCENT }} />
                    <span className="font-bold text-white">{memberCount}</span>/{maxMembers}{' '}
                    Mitglieder
                  </span>
                  {scheduled && (
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarClock size={15} style={{ color: ACCENT }} />
                      {scheduled}
                    </span>
                  )}
                </div>
              </div>

              {/* Aktions-Buttons */}
              <div className="flex flex-col gap-2 flex-shrink-0 md:items-end">
                {!isMember && !isClosed && (
                  <Button
                    className="font-semibold text-zinc-950"
                    style={{ backgroundColor: ACCENT }}
                    startContent={<UserPlus size={16} />}
                    isLoading={actionLoading}
                    isDisabled={isFull}
                    onPress={handleJoin}
                  >
                    {isFull ? 'Lobby ist voll' : 'Lobby beitreten'}
                  </Button>
                )}
                {isMember && !isOwner && (
                  <Button
                    variant="bordered"
                    className="font-semibold text-white border-zinc-700"
                    startContent={<LogOut size={16} />}
                    isLoading={actionLoading}
                    onPress={handleLeave}
                  >
                    Lobby verlassen
                  </Button>
                )}
                {isOwner && (
                  <div className="flex flex-wrap gap-2 md:justify-end">
                    <Button
                      size="sm"
                      variant="bordered"
                      className="font-semibold text-white border-zinc-700"
                      startContent={<Pencil size={14} />}
                      onPress={editModal.onOpen}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      size="sm"
                      variant="bordered"
                      className="font-semibold text-white border-zinc-700"
                      startContent={isClosed ? <DoorOpen size={14} /> : <Lock size={14} />}
                      isLoading={actionLoading}
                      onPress={handleToggleClosed}
                    >
                      {isClosed ? 'Wieder öffnen' : 'Schließen'}
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      variant="flat"
                      className="font-semibold"
                      startContent={<Trash2 size={14} />}
                      onPress={deleteModal.onOpen}
                    >
                      Löschen
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {showLoginHint && !isAuthenticated && (
              <div className="mt-5 rounded-xl border border-zinc-800 bg-zinc-900/80 px-4 py-3 text-sm text-gray-300">
                <RouterLink
                  to="/login"
                  className="inline-flex items-center gap-1.5 font-semibold hover:underline"
                  style={{ color: ACCENT }}
                >
                  <LogIn size={14} />
                  Melde dich an
                </RouterLink>
                , um dieser Lobby beizutreten.
              </div>
            )}
            {actionError && (
              <div className="mt-5 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                <AlertCircle size={16} className="flex-shrink-0" />
                {actionError}
              </div>
            )}

            {/* Status-Banner */}
            {isClosed && (
              <div className="mt-5 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: '#f3126040', backgroundColor: '#f312601A', color: '#f87171' }}
              >
                <Lock size={16} className="flex-shrink-0" />
                Diese Lobby ist geschlossen. Ein Beitritt ist nicht mehr möglich.
              </div>
            )}
            {!isClosed && isFull && !isMember && (
              <div className="mt-5 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: '#f5a52440', backgroundColor: '#f5a5241A', color: '#f5a524' }}
              >
                <Users size={16} className="flex-shrink-0" />
                Diese Lobby ist voll ({memberCount}/{maxMembers}).
              </div>
            )}
          </div>
        </section>

        {/* ============ Inhalt: Mitglieder + Chat ============ */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Mitglieder */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} style={{ color: ACCENT }} />
                <h2 className="text-base font-bold">
                  Mitglieder ({memberCount}/{maxMembers})
                </h2>
              </div>
              {members.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  Noch keine Mitglieder.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {members.map((member, idx) => {
                    const avatarUrl = communityService.buildThumbnailUrl(member.userImage, 100);
                    const isLobbyOwner = member.userId != null && member.userId === lobby.ownerId;
                    const inner = (
                      <div className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 hover:border-zinc-700 p-2.5 transition-colors">
                        <Avatar
                          src={avatarUrl ?? undefined}
                          showFallback
                          fallback={<UserIcon size={16} className="text-gray-500" />}
                          className="h-9 w-9 bg-zinc-800 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">
                            {member.userName ?? 'Unbekannt'}
                          </p>
                          {member.joinedAt && (
                            <p className="text-[11px] text-gray-500">
                              Beigetreten {communityService.formatRelativeTime(member.joinedAt)}
                            </p>
                          )}
                        </div>
                        {isLobbyOwner && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide flex-shrink-0"
                            style={{ color: '#f5a524' }}
                          >
                            <Crown size={12} /> Host
                          </span>
                        )}
                      </div>
                    );
                    return member.userName ? (
                      <RouterLink
                        key={member.userId ?? idx}
                        to={`/spieler/${encodeURIComponent(member.userName)}`}
                        className="block"
                      >
                        {inner}
                      </RouterLink>
                    ) : (
                      <div key={member.userId ?? idx}>{inner}</div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Host-Karte */}
            {lobby.ownerName && (
              <RouterLink
                to={`/spieler/${encodeURIComponent(lobby.ownerName)}`}
                className="flex items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-900/70 hover:border-zinc-700 p-4 transition-colors"
              >
                <Avatar
                  src={ownerAvatarUrl ?? undefined}
                  showFallback
                  fallback={<UserIcon size={18} className="text-gray-500" />}
                  className="h-11 w-11 bg-zinc-800 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                    Host
                  </p>
                  <p className="text-sm font-bold truncate" style={{ color: ACCENT }}>
                    {lobby.ownerName}
                  </p>
                </div>
                <Crown size={18} className="text-amber-400 flex-shrink-0" />
              </RouterLink>
            )}
          </div>

          {/* Lobby-Chat */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 flex flex-col h-[520px]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800">
                <MessageCircle size={18} style={{ color: ACCENT }} />
                <h2 className="text-base font-bold">Lobby-Chat</h2>
              </div>

              {!isMember ? (
                <div className="flex-1 flex items-center justify-center px-6">
                  <div className="text-center">
                    <MessageCircle size={32} className="text-zinc-700 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">
                      Der Chat ist nur für Mitglieder dieser Lobby sichtbar.
                    </p>
                    {!isAuthenticated && (
                      <p className="mt-2 text-sm text-gray-400">
                        <RouterLink
                          to="/login"
                          className="font-semibold hover:underline"
                          style={{ color: ACCENT }}
                        >
                          Melde dich an
                        </RouterLink>
                        , um mitzumachen.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Nachrichtenliste */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="h-full flex items-center justify-center">
                        <p className="text-sm text-gray-500">
                          Noch keine Nachrichten. Schreib die erste!
                        </p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const isMine = msg.senderId != null && msg.senderId === currentUserId;
                        const prev = idx > 0 ? messages[idx - 1] : null;
                        const showDaySeparator =
                          chatService.formatDaySeparator(msg.timestamp) !==
                          chatService.formatDaySeparator(prev?.timestamp ?? null);
                        const senderAvatar = communityService.buildThumbnailUrl(
                          msg.senderImage,
                          100
                        );
                        return (
                          <React.Fragment key={msg.id ?? idx}>
                            {showDaySeparator && msg.timestamp && (
                              <div className="flex justify-center py-1">
                                <span className="px-3 py-0.5 rounded-full bg-zinc-800 text-[10px] font-semibold text-gray-400">
                                  {chatService.formatDaySeparator(msg.timestamp)}
                                </span>
                              </div>
                            )}
                            <div className={`flex gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
                              {!isMine && (
                                <Avatar
                                  src={senderAvatar ?? undefined}
                                  showFallback
                                  fallback={<UserIcon size={12} className="text-gray-500" />}
                                  className="h-7 w-7 bg-zinc-800 flex-shrink-0 mt-0.5"
                                />
                              )}
                              <div
                                className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
                                  isMine
                                    ? 'rounded-br-md text-zinc-950'
                                    : 'rounded-bl-md bg-zinc-800 text-white'
                                }`}
                                style={isMine ? { backgroundColor: ACCENT } : undefined}
                              >
                                {!isMine && msg.senderName && (
                                  <p
                                    className="text-[11px] font-bold mb-0.5"
                                    style={{ color: ACCENT }}
                                  >
                                    {msg.senderName}
                                  </p>
                                )}
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {msg.content ?? ''}
                                </p>
                                <p
                                  className={`text-[10px] mt-0.5 text-right ${
                                    isMine ? 'text-zinc-800' : 'text-gray-500'
                                  }`}
                                >
                                  {chatService.formatMessageTime(msg.timestamp)}
                                </p>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer */}
                  <div className="border-t border-zinc-800 p-3">
                    {sendError && (
                      <p className="mb-2 text-xs text-red-400">{sendError}</p>
                    )}
                    <div className="flex items-end gap-2">
                      <textarea
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Nachricht schreiben …"
                        maxLength={500}
                        rows={1}
                        className="flex-1 resize-none rounded-xl bg-zinc-950 border border-zinc-700 focus:border-[#3fced1] focus:outline-none px-3.5 py-2.5 text-sm text-white placeholder:text-gray-500"
                      />
                      <Button
                        isIconOnly
                        className="text-zinc-950 flex-shrink-0"
                        style={{ backgroundColor: ACCENT }}
                        isLoading={sending}
                        isDisabled={!messageInput.trim()}
                        onPress={handleSendMessage}
                        aria-label="Nachricht senden"
                      >
                        <Send size={16} />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bearbeiten-Modal (Owner) */}
      <LobbyFormModal
        isOpen={editModal.isOpen}
        onOpenChange={editModal.onOpenChange}
        lobby={lobby}
        onSaved={(updated) => setLobby(updated)}
      />

      {/* Löschen-Confirm */}
      <Modal
        isOpen={deleteModal.isOpen}
        onOpenChange={deleteModal.onOpenChange}
        backdrop="blur"
        classNames={{
          base: 'bg-zinc-900 border border-zinc-800',
          header: 'text-white',
          body: 'text-gray-300',
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <Trash2 size={18} className="text-red-400" />
                Lobby wirklich löschen?
              </ModalHeader>
              <ModalBody>
                <p>
                  Die Lobby „{lobby.name ?? ''}“ wird endgültig gelöscht — inklusive aller
                  Nachrichten. Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose} className="text-gray-300">
                  Abbrechen
                </Button>
                <Button
                  color="danger"
                  onPress={handleDelete}
                  isLoading={deleting}
                  startContent={<Trash2 size={16} />}
                >
                  Endgültig löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default LobbyDetailPage;
